import React, { Suspense } from 'react';
import { useRoutes } from 'react-router-dom';
import { routes } from './routes';
import MainLayout from './layouts/MainLayout';
import { AuthProvider } from './contexts/AuthContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { useFeatureFlags } from './contexts/FeatureFlagsContext';
import FeedbackModal from './components/FeedbackModal/FeedbackModal';
import { useFeedback } from './contexts/FeedbackContext';

// LayoutSelector: when the revamp flag is on, OrganizationShellV2 (root layout route)
// provides the full shell for every page, so MainLayout must be skipped entirely.
// When the flag is off (dev only), OrganizationShellV2 is transparent (<Outlet /> only)
// and MainLayout wraps every page as before.
const LayoutSelector: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { useRevamp } = useFeatureFlags();
  if (useRevamp) return <>{children}</>;
  return <MainLayout>{children}</MainLayout>;
};

/**
 * Global feedback modal - automatically displays errors from FeedbackContext
 */
const GlobalFeedbackModal: React.FC = () => {
  const { feedbackState, hideFeedback } = useFeedback();

  return (
    <FeedbackModal
      isOpen={feedbackState.isOpen}
      onClose={hideFeedback}
      type={feedbackState.type}
      title={feedbackState.title}
      message={feedbackState.message}
    />
  );
};

const App: React.FC = () => {
  const element = useRoutes(routes);
  return (
    <FeatureFlagsProvider>
      <FeedbackProvider>
        <AuthProvider>
          <BreadcrumbProvider>
            <LayoutSelector>
              <Suspense fallback={<div className="page-loading">Loading...</div>}>
                {element}
              </Suspense>
            </LayoutSelector>
          </BreadcrumbProvider>
        </AuthProvider>
        <GlobalFeedbackModal />
      </FeedbackProvider>
    </FeatureFlagsProvider>
  );
};

export default App;
